import {
  isReady,
  reconcileFiles,
  validateSelection,
  validateUpload,
  readImagePixels,
} from "./attachmentState";
import {
  dropLocalFile,
  dropLocalFilesOutside,
  keepLocalFile,
} from "./localFiles";

// Owns a single UI session. Every async operation captures its generation.
export default class AttachmentController {
  constructor(client, { imagePixels = readImagePixels } = {}) {
    this.imagePixels = imagePixels;
    this.client = client;
    this.listeners = new Set();
    this.generation = 0;
    this.uploads = new Map();
    this.snapshot = {
      capability: null,
      files: [],
      selected: [],
      pending: [],
      sessionUsageBytes: 0,
      error: null,
      checking: false,
    };
  }
  subscribe = (listener) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  getSnapshot = () => this.snapshot;
  patch(update) {
    this.snapshot = {
      ...this.snapshot,
      ...update,
    };
    this.listeners.forEach((listener) => listener());
  }
  now = () => Date.now() + (this.clockOffset || 0);
  async open(sessionId, historical = []) {
    const generation = ++this.generation;
    this.uploads.forEach((controller) => controller.abort());
    this.uploads.clear();
    this.sessionId = sessionId;
    dropLocalFilesOutside(sessionId);
    this.clockOffset = 0;
    this.patch({
      files: [
        ...new Map(
          historical.map((file) => [
            file.id,
            {
              ...file,
              status: "unverified",
            },
          ]),
        ).values(),
      ],
      selected: [],
      pending: [],
      capability: null,
      sessionUsageBytes: 0,
      error: null,
      checking: !!sessionId,
    });
    if (!this.client || !sessionId) {
      this.patch({
        checking: false,
      });
      return;
    }
    try {
      const capability = await this.client.capability();
      if (generation !== this.generation) return;
      this.patch({
        capability,
      });
      if (capability.enabled) await this.refresh(generation);
      else
        this.patch({
          checking: false,
        });
    } catch (error) {
      if (generation === this.generation)
        this.patch({
          checking: false,
          error: error.detail || {
            message: error.message,
          },
        });
    }
  }
  close() {
    ++this.generation;
    this.uploads.forEach((controller) => controller.abort());
    this.uploads.clear();
  }
  async refresh(generation = this.generation) {
    const result = await this.client.list(this.sessionId);
    if (generation !== this.generation) return;
    this.clockOffset = Date.parse(result.serverTime) - Date.now();
    const files = reconcileFiles(this.snapshot.files, result.attachments);
    this.patch({
      files,
      sessionUsageBytes: result.sessionUsageBytes,
      checking: false,
      selected: this.snapshot.selected.filter((id) =>
        files.some((file) => file.id === id && isReady(file, this.now())),
      ),
    });
    return files;
  }
  // The original of one file, for a conversation reopened from History. A file already known to be gone is not asked for.
  async loadOriginal(id) {
    const file = this.snapshot.files.find((item) => item.id === id);
    if (!file || !this.client?.content || !this.sessionId)
      throw new Error("original unavailable");
    if (file.status !== "unverified" && !isReady(file, this.now()))
      throw new Error("original expired");
    const blob = await this.client.content(this.sessionId, id);
    return new File([blob], file.name || "file", {
      type: file.mimeType || blob.type,
    });
  }
  toggle(id) {
    if (
      !this.snapshot.files.some(
        (file) => file.id === id && isReady(file, this.now()),
      )
    )
      return;
    const selected = this.snapshot.selected;
    this.patch({
      selected: selected.includes(id)
        ? selected.filter((value) => value !== id)
        : [...selected, id],
      error: null,
    });
  }
  async upload(file, retryKey) {
    const capability = this.snapshot.capability;
    if (!capability?.enabled || !this.sessionId) return;
    const generation = this.generation;
    const key = retryKey || `local-${++AttachmentController.sequence}`;
    const entry = {
      key,
      file,
      name: file.name,
      progress: 0,
      status: "pending",
    };
    this.patch({
      pending: [
        ...this.snapshot.pending.filter((item) => item.key !== key),
        entry,
      ],
      error: null,
    });
    const update = (fields) => {
      if (generation === this.generation)
        this.patch({
          pending: this.snapshot.pending.map((item) =>
            item.key === key
              ? {
                  ...item,
                  ...fields,
                }
              : item,
          ),
        });
    };
    const pendingBytes = () =>
      this.snapshot.pending
        .filter(
          (item) =>
            item.key !== key && ["pending", "uploading"].includes(item.status),
        )
        .reduce((sum, item) => sum + item.file.size, 0);
    const localError = validateUpload(
      file,
      capability,
      this.snapshot.sessionUsageBytes,
      pendingBytes(),
    );
    if (localError) {
      update({
        status: "error",
        error: localError,
      });
      return;
    }
    const controller = new AbortController();
    this.uploads.set(key, controller);
    const knownIds = new Set(this.snapshot.files.map((item) => item.id));
    try {
      if (file.type.startsWith("image/")) {
        let pixels;
        try {
          pixels = await this.imagePixels(file);
        } catch (error) {
          throw Object.assign(new Error(`${file.name}：圖片無法讀取。`), {
            detail: {
              code: "INVALID_FILE",
              message: `${file.name}：圖片無法讀取。`,
              retryable: false,
              notices: capability.notices,
            },
          });
        }
        if (generation !== this.generation) return;
        if (controller.signal.aborted)
          throw new DOMException("已取消", "AbortError");
        const pixelError = validateUpload(
          file,
          capability,
          this.snapshot.sessionUsageBytes,
          pendingBytes(),
          pixels,
        );
        if (pixelError)
          throw Object.assign(new Error(pixelError.message), {
            detail: pixelError,
          });
      }
      update({
        status: "uploading",
      });
      const result = await this.client.upload(this.sessionId, file, {
        signal: controller.signal,
        onProgress: (progress) =>
          update({
            progress,
          }),
      });
      if (generation !== this.generation) return;
      // Cancellation after server completion needs list reconciliation, not optimistic quota release.
      if (controller.signal.aborted) {
        update({
          status: "cancelled",
        });
        // Root finished the upload before the cancel reached it: the user did not want this file, so remove it.
        await this.client.remove(this.sessionId, result.attachment.id);
        await this.refresh(generation);
        return;
      }
      keepLocalFile(result.attachment.id, this.sessionId, file);
      this.patch({
        files: [...this.snapshot.files, result.attachment],
        selected: [...this.snapshot.selected, result.attachment.id],
        sessionUsageBytes: result.sessionUsageBytes,
        pending: this.snapshot.pending.filter((item) => item.key !== key),
      });
    } catch (error) {
      update({
        status: controller.signal.aborted ? "cancelled" : "error",
        error: error.detail || {
          message: error.message,
        },
      });
      if (generation === this.generation && controller.signal.aborted) {
        try {
          await this.refresh(generation);
          await this.dropLateArrivals(generation, file, knownIds);
        } catch (refreshError) {
          if (generation === this.generation)
            this.patch({
              error: {
                message: refreshError.message,
              },
            });
        }
      }
    } finally {
      if (generation === this.generation) this.uploads.delete(key);
    }
  }
  // An aborted request can still complete on Root. Whatever ready file with this name and size appeared since the
  // upload started is that upload: remove it, or it comes back on screen after the cancel.
  async dropLateArrivals(generation, file, knownIds) {
    const late = this.snapshot.files.filter(
      (item) =>
        !knownIds.has(item.id) &&
        item.status === "ready" &&
        item.name === file.name &&
        item.sizeBytes === file.size,
    );
    if (!late.length) return;
    await Promise.all(
      late.map((item) => this.client.remove(this.sessionId, item.id)),
    );
    await this.refresh(generation);
  }
  cancel(key) {
    this.uploads.get(key)?.abort();
  }
  discardPending(key) {
    this.cancel(key);
    this.patch({
      pending: this.snapshot.pending.filter((item) => item.key !== key),
    });
  }
  async remove(id) {
    const generation = this.generation;
    try {
      await this.client.remove(this.sessionId, id);
      dropLocalFile(id);
      if (generation !== this.generation) return;
      this.patch({
        files: this.snapshot.files.map((file) =>
          file.id === id
            ? {
                ...file,
                status: "revoked",
              }
            : file,
        ),
        selected: this.snapshot.selected.filter((value) => value !== id),
        error: null,
      });
      await this.refresh(generation);
    } catch (error) {
      if (generation === this.generation)
        this.patch({
          error: error.detail || {
            message: error.message,
          },
        });
    }
  }
  validation(model, includeVideo, ids = this.snapshot.selected) {
    return validateSelection({
      ...this.snapshot,
      ids,
      model,
      includeVideo,
      now: this.now(),
    });
  }
  async prepare(model, includeVideo, ids = [...this.snapshot.selected]) {
    const generation = this.generation;
    if (ids.length) await this.refresh(generation);
    if (generation !== this.generation) throw new Error("對話已切換。");
    const error = this.validation(model, includeVideo, ids);
    if (error) {
      this.patch({
        error,
      });
      throw Object.assign(new Error(error.message), {
        detail: error,
      });
    }
    return ids.map((id) => ({
      ...this.snapshot.files.find((file) => file.id === id),
    }));
  }
}
AttachmentController.sequence = 0;
